import { useCallback, useEffect, useRef, useState } from 'react';

interface ThinkingVerbsJson {
  thinking_verbs: string[];
}

interface UsePromptExplanationStreamReturn {
  thinkingVerbs: string[];
  explanationText: string;
  isStreaming: boolean;
  reset: () => void;
  processChunk: (chunk: string) => void;
}

/**
 * Hook to parse and manage prompt explanation streaming content
 *
 * Handles:
 * - Parsing JSON with thinking_verbs from code blocks
 * - Extracting explanation text after code blocks
 * - Separating thinking verbs from actual explanation content
 */
export const usePromptExplanationStream = (): UsePromptExplanationStreamReturn => {
  const [thinkingVerbs, setThinkingVerbs] = useState<string[]>([]);
  const [explanationText, setExplanationText] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const rawContentRef = useRef('');
  const codeBlockEndRef = useRef(false);

  const reset = useCallback(() => {
    setThinkingVerbs([]);
    setExplanationText('');
    setIsStreaming(false);
    rawContentRef.current = '';
    codeBlockEndRef.current = false;
  }, []);

  const processChunk = useCallback((chunk: string) => {
    setIsStreaming(true);
    rawContentRef.current += chunk;

    const content = rawContentRef.current;

    // Try to find and parse JSON code block with thinking_verbs
    if (!codeBlockEndRef.current) {
      // Match code block from ```json to ```
      const codeBlockRegex = /```json\s*([\s\S]*?)```/;
      const codeBlockMatch = content.match(codeBlockRegex);

      if (codeBlockMatch) {
        codeBlockEndRef.current = true;
        const jsonContent = codeBlockMatch[1].trim();

        // Try to parse JSON
        try {
          const parsed = JSON.parse(jsonContent) as ThinkingVerbsJson;
          if (parsed.thinking_verbs && Array.isArray(parsed.thinking_verbs)) {
            setThinkingVerbs(parsed.thinking_verbs);
          }
        } catch {
          // If full JSON parse fails, try to extract thinking_verbs directly
          const verbsMatch = jsonContent.match(/"thinking_verbs"\s*:\s*\[(.*?)\]/s);
          if (verbsMatch) {
            try {
              const verbsArray = JSON.parse(`[${verbsMatch[1]}]`) as string[];
              if (Array.isArray(verbsArray)) {
                setThinkingVerbs(verbsArray);
              }
            } catch {
              // Try to parse individual quoted strings
              const stringMatches = verbsMatch[1].matchAll(/"([^"]+)"/g);
              const verbs: string[] = [];
              for (const match of stringMatches) {
                verbs.push(match[1]);
              }
              if (verbs.length > 0) {
                setThinkingVerbs(verbs);
              }
            }
          }
        }

        // Extract text after code block
        const afterCodeBlock = content.split(codeBlockRegex)[2];
        if (afterCodeBlock) {
          setExplanationText(afterCodeBlock.trim());
        }
      } else {
        // Check if we have a partial JSON code block - try to parse it incrementally
        const partialCodeBlockMatch = content.match(/```json\s*([\s\S]*)/);
        if (partialCodeBlockMatch) {
          const jsonPart = partialCodeBlockMatch[1];
          // Try to extract thinking_verbs if JSON is complete enough
          const verbsMatch = jsonPart.match(/"thinking_verbs"\s*:\s*\[(.*?)\]/s);
          if (verbsMatch) {
            try {
              const verbsArray = JSON.parse(`[${verbsMatch[1]}]`) as string[];
              if (Array.isArray(verbsArray)) {
                setThinkingVerbs(verbsArray);
              }
            } catch {
              // Try to parse individual strings
              const stringMatches = verbsMatch[1].matchAll(/"([^"]+)"/g);
              const verbs: string[] = [];
              for (const match of stringMatches) {
                verbs.push(match[1]);
              }
              if (verbs.length > 0) {
                setThinkingVerbs(verbs);
              }
            }
          }
        }
      }
    } else {
      // Code block already processed, append to explanation text
      const codeBlockRegex = /```json\s*([\s\S]*?)```/;
      const afterCodeBlock = content.split(codeBlockRegex)[2];
      if (afterCodeBlock) {
        setExplanationText(afterCodeBlock.trim());
      }
    }
  }, []);

  // Keep streaming state active while we're receiving content
  // Only mark as complete when we have explanation text and code block is done
  useEffect(() => {
    if (!isStreaming && codeBlockEndRef.current && explanationText) {
      // Streaming completed, but don't reset isStreaming here
      // Let the parent component manage when streaming ends
    }
  }, [isStreaming, explanationText]);

  return {
    thinkingVerbs,
    explanationText,
    isStreaming,
    reset,
    processChunk,
  };
};
